import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  MoneyDTO,
  Notification,
  PaymentNotification,
  PaymentNotificationContext,
  RepaymentNotification,
  RepaymentNotificationContext,
  SynchronizationNotificationContext
} from 'storage/notifications/notifications.model';
import { formatCurrency } from 'utils/formatCurrency';
import { Constant } from 'utils/consts';

import { Link } from '@mui/material';
import { Url } from 'utils/urls';
import { TWO_WEEKS_PERIOD } from '../../../../utils/getInstallmentsData/getInstallmentsNotifications';

export function isPaymentNotificationContext(
  context: Notification['context']
): context is PaymentNotificationContext {
  return (
    'paymentAmount' in context &&
    'daysUntilDueDate' in context &&
    'sourceContractId' in context
  );
}

export function isPaymentNotificationType(
  notificaton: Notification
): notificaton is PaymentNotification {
  return (
    'paymentAmount' in notificaton.context &&
    'daysUntilDueDate' in notificaton.context
  );
}

export function isRepaymentNotificationType(
  notificaton: Notification
): notificaton is RepaymentNotification {
  return (
    isRepaymentNotificationContext(notificaton.context) &&
    notificaton.type === 'REPAYMENT'
  );
}

export function isBadgeNotificationType(
  notification: Notification
): notification is PaymentNotification | RepaymentNotification {
  return (
    isPaymentNotificationType(notification) ||
    isRepaymentNotificationType(notification)
  );
}

export function isRepaymentNotificationContext(
  context: Notification['context']
): context is RepaymentNotificationContext {
  return 'paymentAmount' in context && 'contractId' in context;
}

// payment or repayment
export function isFinanceRelatedContext(
  context: Notification['context']
): context is Extract<
  Notification['context'],
  { paymentAmount: MoneyDTO; contractId: number }
> {
  return 'paymentAmount' in context && 'contractId' in context;
}

export function isSynchronizationContext(
  context: Notification['context']
): context is SynchronizationNotificationContext {
  return 'synchronizationTime' in context;
}
// replace with distributions of Notification type when dust settles
type PaymentRelatedNotification = Notification & {
  context: PaymentNotificationContext | RepaymentNotificationContext;
};

export function isFinanceRelatedNotification(
  notification: Notification
): notification is PaymentRelatedNotification {
  return isFinanceRelatedContext(notification.context);
}

const useNotifications = () => {
  const { t } = useTranslation();
  const getNotificationText = (
    name: Notification['name'],
    context: Notification['context'],
    messageKey: Notification['messageKey'] = 'MISSING TEXT KEY'
  ): ReactNode => {
    const isInstallment = name.includes('INSTALLMENT')
      ? 'INSTALLMENT'
      : 'DEFAULT';
    const isMultipleInstallmentsOverdue = name.includes('INSTALLMENTS');

    if (isFinanceRelatedContext(context)) {
      const { amount, currency } = context.paymentAmount;
      const money = (
        formatCurrency(
          'plain',
          amount,
          currency,
          true,
          true,
          true,
          true
        ) as string
      ).replace(/ /, '\u00a0');

      if (isMultipleInstallmentsOverdue) {
        return t('COMMON_NOTIFICATION_PAST_INSTALLMENTS', { money });
      }

      if (isPaymentNotificationContext(context)) {
        const { daysUntilDueDate } = context;
        if (daysUntilDueDate < Constant.zero) {
          return t(`COMMON_NOTIFICATION_PAST_${isInstallment}`, { money });
        }
        if (daysUntilDueDate === Constant.zero) {
          return t(`COMMON_NOTIFICATION_TODAY_${isInstallment}`, { money });
        }
        if (
          daysUntilDueDate < TWO_WEEKS_PERIOD &&
          daysUntilDueDate > Constant.zero
        ) {
          return t(`COMMON_NOTIFICATION_FUTURE_${isInstallment}_interval`, {
            postProcess: 'interval',
            count: daysUntilDueDate,
            money
          });
        }
      }

      if (isRepaymentNotificationContext(context)) {
        return t(messageKey, {
          amount: money
        });
      }
    }
    if (isSynchronizationContext(context)) {
      return (
        <Trans
          i18nKey={messageKey}
          values={{ synchronizationTime: context.synchronizationTime }}
        >
          Prezentowane dane mogą być nieaktualne (ostatnia synchronizacja:
          synchronizationTime). Aby uzyskać więcej informacji
          <Link
            href={`${Url.loggedContact}`}
            sx={{ fontWeight: 700, cursor: 'pointer' }}
          >
            skontaktuj się z nami.
          </Link>
        </Trans>
      );
    }
    return t(messageKey);
  };

  const manageNotificationDisplayedDates = ({
    name,
    context
  }: {
    name: Notification['name'];
    context: Notification['context'];
  }): string => {
    if (
      isPaymentNotificationContext(context) &&
      name.includes('PAYMENT_DUE_SOON')
    ) {
      const date = new Date();
      date.setDate(date.getDate() + context.daysUntilDueDate);
      return date.toLocaleDateString();
    }
    if (isRepaymentNotificationContext(context)) {
      return context.postingDate;
    }
    return '';
  };

  return { getNotificationText, manageNotificationDisplayedDates };
};

export default useNotifications;
